/**
 * Foot-contact proxy — the physical-reach truth for any footed ball action. (§7.1)
 *
 * A player may only strike, poke or dribble-touch a ball their FOOT can actually
 * reach this tick. "Reach" is a surface-to-surface fact, NOT a possession one:
 * owning a ball at POSSESSION_RADIUS (1.2m) does not put a boot on it. Measured
 * live (Phase-2A contact truth), the loose-ball strike already gated on
 * BALL_PLAY_REACH, but the OWNER strike and the dribble touch did not — so passes
 * fired with the ball ~0.7m off the foot and shots ~1.2m off it, which is exactly
 * what "the ball moves before a player reaches it" looks like from the stands.
 *
 * This module is the single source of that reach test. Every footed contact site
 * gates on `inFootReach`; the contact log records the full `footContact` geometry
 * so the invariant is checkable over real play.
 *
 * Pure and deterministic: Math.sqrt is IEEE-754 exact on every engine (unlike
 * sin/cos/tan/atan2/pow, it is not sim-purity-banned), so this needs no d*
 * wrapper. It only ever reads scalars — it never touches WorldState.
 */
import {
  BALL_PLAY_REACH,
  BALL_RADIUS,
  PLAYER_CAPSULE_RADIUS,
  STRIKE_MAX_HEIGHT,
} from './constants.js';

/** Centre-to-centre distance at which a capsule and the ball surfaces just meet. */
export const CONTACT_CENTRE_DIST = PLAYER_CAPSULE_RADIUS + BALL_RADIUS; // 0.41 m

/** How far below the foot plane still counts as "at the foot" (grounded ball). */
const FOOT_UNDER_MARGIN = CONTACT_CENTRE_DIST;

/** The foot-contact geometry between one player and the ball, in the XZ plane. */
export interface FootContact {
  /** Horizontal player-centre to ball-centre distance (m). */
  centreDistXZ: number;
  /** Surface-to-surface gap: centreDistXZ − CONTACT_CENTRE_DIST. <0 ⇒ overlapping. */
  surfaceGap: number;
  /** Ball-centre height above the player's foot plane (m). */
  heightAboveFoot: number;
  /** Boot proxy point: the capsule surface toward the ball, at foot height. */
  footX: number;
  footZ: number;
  /** True ⇒ a boot can physically touch the ball this tick. */
  inReach: boolean;
}

/**
 * True when the ball is inside the boot's physical reach: within BALL_PLAY_REACH
 * horizontally AND within the footed-strike vertical envelope. This is the same
 * horizontal reach the loose-ball strike scan already used — we simply hold every
 * footed action to it, owner strikes included.
 */
export function inFootReach(
  px: number,
  pz: number,
  py: number,
  bx: number,
  by: number,
  bz: number,
): boolean {
  const dx = px - bx;
  const dz = pz - bz;
  const d = Math.sqrt(dx * dx + dz * dz);
  const h = by - py;
  return d <= BALL_PLAY_REACH && h <= STRIKE_MAX_HEIGHT && h >= -FOOT_UNDER_MARGIN;
}

/** Full foot-contact geometry, for the contact log and diagnostics. */
export function footContact(
  px: number,
  pz: number,
  py: number,
  bx: number,
  by: number,
  bz: number,
): FootContact {
  const dx = bx - px;
  const dz = bz - pz;
  const centreDistXZ = Math.sqrt(dx * dx + dz * dz);
  const heightAboveFoot = by - py;
  // Boot proxy: step from the player centre toward the ball by one capsule radius,
  // at foot height. Degenerate (ball on the axis) → sit the proxy at the centre.
  let footX = px;
  let footZ = pz;
  if (centreDistXZ > 1e-6) {
    footX = px + (dx / centreDistXZ) * PLAYER_CAPSULE_RADIUS;
    footZ = pz + (dz / centreDistXZ) * PLAYER_CAPSULE_RADIUS;
  }
  return {
    centreDistXZ,
    surfaceGap: centreDistXZ - CONTACT_CENTRE_DIST,
    heightAboveFoot,
    footX,
    footZ,
    inReach:
      centreDistXZ <= BALL_PLAY_REACH &&
      heightAboveFoot <= STRIKE_MAX_HEIGHT &&
      heightAboveFoot >= -FOOT_UNDER_MARGIN,
  };
}
